import tinycolor from 'tinycolor2';
import type { Theme } from './themes';
import { clamp } from 'lodash';

const hex2rgba = function (color: string, alpha?: number) {
  if (!tinycolor(color).isValid()) {
    throw new Error(`Invalid color format: ${color}`);
  }

  const rgba = tinycolor(color).toRgb();
  const { r, g, b } = rgba;

  if (alpha !== undefined) {
    return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
  }
  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Converts an RGB colour to another lighter RGB colour, given an opacity,
 * assuming we have a white background (i.e. does not set an Alpha channel)
 * @param color The base colour as an RGB Hex string
 * @param opacity Desired opacity of the provided color, between 0 and 1
 */
const opacify = function (color: string, opacity: number) {
  return tinycolor.mix('#FFF', color, clamp(opacity, 0, 1) * 100).toHexString();
};

/**
 * Converts an RGB colour to another darker RGB colour, given an opacity,
 * assuming we have a black background (i.e. does not set an Alpha channel)
 *
 * WARNING: this implementation fails to darken light colors. Consider using {@link shade} instead.
 *
 * @param color The base colour as an RGB Hex string
 * @param opacity Desired opacity of the black background, between 0 and 1
 */
const opacifyDark = function (color: string, opacity: number) {
  const rgba = tinycolor(color).toRgb();
  const convertChannel = (channel: number): number => channel * (1 + opacity) - opacity * 255;
  return tinycolor({
    r: convertChannel(rgba.r),
    g: convertChannel(rgba.g),
    b: convertChannel(rgba.b),
    a: 1,
  }).toHexString();
};

/**
 * Mixes a color towards the opposite brightness value, such that dark colors become lighter and light colors become darker.
 */
const shade = (color: string, shadeFraction = 0.1) => {
  const tColor = tinycolor(color);
  const shadeFactor = clamp(shadeFraction, 0, 1) * 100;
  const newColor = tColor.getBrightness() > 128 ? tColor.darken(shadeFactor) : tColor.lighten(shadeFactor);
  return newColor.toHexString();
};

/** Mixes colors together, without preserving brightness. */
export const mixColors = (color1: string, color2: string, ratio: number) => {
  return tinycolor.mix(color1, color2, clamp(ratio, 0, 1) * 100).toHexString();
};

export const ColorUtils = {
  hex2rgba,

  opacify,

  opacifyDark,

  shade,

  mix: mixColors,

  hex2rgbaFrom: (colorSelector: (props: { theme: Theme }) => string, alpha?: number) => (props: { theme: Theme }) => {
    const color = colorSelector(props);
    return hex2rgba(color, alpha);
  },

  opacifyFrom: (colorSelector: (props: { theme: Theme }) => string, opacity: number) => (props: { theme: Theme }) => {
    const color = colorSelector(props);
    return opacify(color, opacity);
  },

  opacifyDarkFrom:
    (colorSelector: (props: { theme: Theme }) => string, opacity: number) => (props: { theme: Theme }) => {
      const color = colorSelector(props);
      return opacifyDark(color, opacity);
    },

  shadeFrom:
    (colorSelector: (props: { theme: Theme }) => string, shadeFactor = 0.1) =>
    (props: { theme: Theme }) => {
      return shade(colorSelector(props), shadeFactor);
    },

  mixFrom:
    (
      colorSelector: (props: { theme: Theme }) => string,
      colorToMixSelector: (props: { theme: Theme }) => string,
      ratio: number,
    ) =>
    (props: { theme: Theme }) => {
      return mixColors(colorSelector(props), colorToMixSelector(props), ratio);
    },
};

/**
 * A DOM node created once, globally, used for testing if a color is a valid CSS color.
 * This allows us to reuse the same dom node rather than creating new ones constantly during rendering.
 * The state of the node must be reset after a test, so that future tests aren't affected.
 */
const colorTestNode = new Option().style;
/** Returns true if the provided string represents a valid CSS color (such as #00F) or CSS color keyword (such as 'initial', 'inherit', 'unset', or 'red'). */
export const isColor = (color: string | undefined | null): color is string => {
  if (!color) {
    return false;
  }

  colorTestNode.color = color;
  const isValidColor = colorTestNode.color !== '';

  // Reset the node's color value for the next test
  colorTestNode.color = '';

  return isValidColor;
};
